import React, { useState, useEffect } from 'react';
import { Alert, Modal, Button, Form, Spinner } from 'react-bootstrap';

const isDev = process.env.NODE_ENV === 'development';

interface Props {
  show: boolean;
  onHide: () => void;
  onSwitch: (username: string) => void;
  currentUsername?: string;
  allowedUsernames?: string[];
  isAdmin?: boolean;
  switchedFromUsername?: string;
  isEmailLogin?: boolean;
}

const SwitchUserDropdown: React.FC<Props> = ({ show, onHide, onSwitch, currentUsername, allowedUsernames, isAdmin, switchedFromUsername, isEmailLogin }) => {
  const [usernames, setUsernames] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryUsername, setPrimaryUsername] = useState('');
  const [roles, setRoles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!show) return;

    const normalizedAllowedUsernames = (allowedUsernames || []).filter(Boolean);

    if (isEmailLogin) {
      setLoading(true);
      setError(null);
      fetch((isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com') + '/user/accounts', {
        headers: { Authorization: localStorage.getItem('authToken') || '' }
      })
        .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
        .then(data => {
          const accounts = Array.isArray(data.accounts) ? data.accounts : [];
          setUsernames(accounts.map((account: any) => account.username));
          setRoles(Object.fromEntries(accounts.map((account: any) => [account.username, account.role])));
          setPrimaryUsername(data.primaryUsername || '');
          setSelected(currentUsername && accounts.some((account: any) => account.username === currentUsername)
            ? currentUsername
            : accounts[0]?.username || '');
          setLoading(false);
        })
        .catch(() => { setError('Failed to load accounts'); setLoading(false); });
      return;
    }

    if (!isAdmin && !switchedFromUsername && normalizedAllowedUsernames.length) {
      const filtered = normalizedAllowedUsernames.filter(u => u.toLowerCase() !== (currentUsername || '').toLowerCase());
      setUsernames(filtered);
      setSelected(filtered[0] || '');
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    fetch((isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com') + '/user/list-usernames', {
      headers: { Authorization: localStorage.getItem('authToken') || '' }
    })
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then(data => {
        const current = (currentUsername || '').toLowerCase();
        const filtered = data.filter((u: string) => u.toLowerCase() !== current);
        setUsernames(filtered);
        setSelected(filtered[0] || '');
        setLoading(false);
      })
      .catch(err => { setError('Failed to load users'); setLoading(false); });
  }, [show, currentUsername, allowedUsernames, isAdmin, switchedFromUsername, isEmailLogin]);

  const handleSwitch = () => {
    if (selected) onSwitch(selected);
  };

  const handleSetPrimary = async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch((isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com') + '/user/accounts/primary', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({ username: selected })
      });
      if (!response.ok) throw new Error();
      setPrimaryUsername(selected);
    } catch {
      setError('Failed to update the primary account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Switch User</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? <Spinner animation="border" /> : usernames.length ? (
          <Form>
            <Form.Group controlId="switchUserSelect">
              <Form.Label>Select user</Form.Label>
              <Form.Select value={selected} onChange={e => setSelected(e.target.value)}>
                {usernames.map(u => (
                  <option key={u} value={u}>{u}{roles[u] ? ` (${roles[u]})` : ''}{u === primaryUsername ? ' — primary' : ''}</option>
                ))}
              </Form.Select>
              {isEmailLogin && <Form.Text className="text-muted">The primary account opens automatically at your next e-mail login.</Form.Text>}
            </Form.Group>
          </Form>
        ) : !error ? <div>No accounts available.</div> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        {isEmailLogin && <Button variant="outline-primary" onClick={handleSetPrimary} disabled={!selected || loading || selected === primaryUsername}>Set as primary</Button>}
        <Button variant="primary" onClick={handleSwitch} disabled={!selected || loading}>Switch</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SwitchUserDropdown;
